import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Check, ArrowRight, Sparkles, TrendingUp, MessageCircle } from "lucide-react";
import { PLAN_URLS } from "../../api/PlanUrls";
import { post } from "../../services/smartApiService";
import { showAlertAutoClose, showNotification1 } from "../../services/notifyService";
import { useSiteContext } from "../../contexts/SiteProvider";


import "./PlanUpgrade.scss";
import { openExternalLink } from "../../services/site/linkService";
import { InfoService } from "../../services/site/InfoService";

interface PlanUpgradeProps {
  category?: string;
  currentPlanName?: string;
  userId?: number | string;
  onUpgradeSuccess?: () => void;
  onFreePlanSelect?: () => void;
}

interface Plan {
  ID: number;
  name: string;
  price: string;
  currency: string;
  description: string;
  billingInterval: string;
  features: any[];
  isActive?: boolean;
  trial_days?: number;
}

const PlanUpgrade: React.FC<PlanUpgradeProps> = ({
  category = "digisign",
  currentPlanName = "",
  userId,
  onUpgradeSuccess,
  onFreePlanSelect,
}) => {
  const [upgradePlans, setUpgradePlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isYearly, setIsYearly] = useState(false);
  const [processingPlanId, setProcessingPlanId] = useState<number | null>(null);
  const [isSubmittingEnterprise, setIsSubmittingEnterprise] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const enterpriseTextRef = React.useRef<string>("");
  const { user,  openModal, closeModal } = useSiteContext();
  console.log("Current Plan Name in Upgrade Component:", currentPlanName);
  const yearlyDiscount = 0.15;


 

  const isEnterprisePlan = (planName: string) => {
    return planName.toLowerCase() === "enterprise";
  };

  useEffect(() => {
    loadUpgradePlans();
  }, [ category]);

  const loadUpgradePlans = async () => {
    setIsLoading(true);
    try {
      const subscription = post(PLAN_URLS.GET_PLANS, { category }).subscribe(
        ({ data }) => {
          const plans: Plan[] = data.data ?? [];
          const availablePlans =plans
          console.log("Available Upgrade Plans:", availablePlans);
          setUpgradePlans(availablePlans);
          setIsLoading(false);
        }
      );
      return () => subscription.unsubscribe();
    } catch (error) {
      console.error("Error loading plans:", error);
      setIsLoading(false);
    }
  };



  const calculatePrice = (basePrice: string, yearly: boolean): number => {
    const price = parseFloat(basePrice || "0");
    return yearly ? price * 12 * (1 - yearlyDiscount) : price;
  };





 

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case "INR":
        return "₹";
      case "USD":
        return "$";
      case "EUR":
        return "€";
      case "GBP":
        return "£";
      default:
        return "$";
    }
  };

  const handleEnterpriseInquiry = () => {
    enterpriseTextRef.current = "";

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      enterpriseTextRef.current = e.target.value;
      const charCount = document.getElementById('char-count');
      if (charCount) {
        charCount.textContent = `${e.target.value.length} characters entered`;
        charCount.style.display = e.target.value.trim() ? 'block' : 'none';
      }
      const submitBtn = document.getElementById('enterprise-submit-btn') as HTMLButtonElement;
      if (submitBtn) {
        submitBtn.disabled = !e.target.value.trim() || isSubmittingEnterprise;
      }
    };

  
  

    const modalContent = (
      <div className="enterprise-modal-content">
        <div className="mb-5">
          <h3 className="title is-4 has-text-centered mb-3" style={{ color: '#667eea' }}>
            <MessageCircle size={28} style={{ verticalAlign: 'middle', marginRight: '10px' }} />
            Enterprise Plan Inquiry
          </h3>
          <p className="has-text-centered has-text-grey">
            Tell us about your business needs and we'll create a custom solution for you.
          </p>
        </div>

        <div className="field">
          <label className="label has-text-weight-semibold">
            Your Requirements <span className="has-text-danger">*</span>
          </label>
          <div className="control">
            <textarea
              id="enterprise-textarea"
              className="textarea"
              placeholder="Please describe:
• Your team size
• Required features
• Expected usage volume
• Any specific integration needs
• Timeline expectations"
              rows={8}
              onChange={handleTextChange}
              style={{ resize: 'vertical' }}
            />
          </div>
          <p id="char-count" className="help is-success" style={{ display: 'none' }}>
            0 characters entered
          </p>
        </div>

        <div className="buttons is-right mt-5">
          <button
            className="button is-light"
          
          >
            Cancel
          </button>
          <button
            id="enterprise-submit-btn"
            className="button is-primary"
            // disabled={true}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none'
            }}
          >
            <MessageCircle size={18} style={{ marginRight: '8px' }} />
            Submit Inquiry
          </button>
        </div>
      </div>
    );

    openModal({
      title: "",
      content: modalContent,
      width: 650,
      closeBody: false,
    });
  };

  if (isLoading) {
    return (
      <div className="plan-upgrade-loading">
        <div className="plan-upgrade-spinner">
          <Sparkles className="spinning" />
        </div>
        <p className="has-text-centered mt-4">Loading upgrade options...</p>
      </div>
    );
  }

  if (upgradePlans.length === 0) {
    return (
      <div className="plan-upgrade-max">
        <div className="plan-upgrade-max-content">
          <div className="plan-upgrade-max-icon">
            <Zap />
          </div>
          <h2 className="title is-4">You're on the Best Plan!</h2>
          <p className="has-text-grey">
            You already have access to all premium features.
          </p>
        </div>
      </div>
    );
  }

 

  return (
    <div className="plan-upgrade-container">
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
          className="plan-upgrade-wrapper">
          {/* Header Section */}
          {/* <div className="plan-upgrade-header">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="plan-upgrade-icon-badge">
              <TrendingUp />
            </motion.div>
            <h2 className="title is-3 has-text-centered mb-2">
              Upgrade Your Plan
            </h2>
            <p className="has-text-centered has-text-grey">
              Unlock more features and boost your productivity
            </p>
          </div> */}

          {/* Billing Toggle */}
          <div className="plan-upgrade-billing-toggle">
            <span className={!isYearly ? "is-active" : ""}>Monthly</span>
            <label className="plan-upgrade-switch">
              <input
                type="checkbox"
                checked={isYearly}
                onChange={() => setIsYearly(!isYearly)}
              />
              <span className="plan-upgrade-slider"></span>
            </label>
            <span className={isYearly ? "is-active" : ""}>
              Yearly
              <span className="tag is-success is-light ml-2">Save 15%</span>
            </span>
          </div>

          {/* Plan Cards Grid - Using Bulma Columns */}
          <div className="columns is-variable is-4 is-multiline is-centered">
            {upgradePlans.map((plan, index) => {
              const displayPrice = calculatePrice(plan.price, isYearly);
              const currencySymbol = getCurrencySymbol(plan.currency);
              const isEnterprise = isEnterprisePlan(plan.name);
              const isFree = plan.name.toLowerCase() === "free" || parseFloat(plan.price) === 0;
              // Don't recommend Free plan
              const isRecommended = !isFree && !isEnterprise && index === 0;
              const isProcessing = processingPlanId === plan.ID;

              return (
                <div
                  key={plan.ID}
                  className="column is-narrow is-3-desktop is-6-tablet is-12-mobile"
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className={`plan-upgrade-card ${isRecommended ? 'recommended' : ''}`}>
                    {isRecommended && (
                      <div className="plan-upgrade-card-badge">
                        <Sparkles size={16} />
                        <span>Recommended</span>
                      </div>
                    )}

                  <div className="plan-upgrade-card-content">
                    <h3 className="title is-4 mb-3">{plan.name}</h3>
                    <p className="has-text-grey mb-4">
                      {plan.description}
                    </p>

                    {/* Hide price for Enterprise plans */}
                    {!isEnterprise && (
                      <>
                        <div className="plan-upgrade-price">
                          <span className="plan-upgrade-price-currency">
                            {currencySymbol}
                          </span>
                          <span className="plan-upgrade-price-amount">
                            {displayPrice.toFixed(0)}
                          </span>
                          <span className="plan-upgrade-price-interval">
                            /{isYearly ? "year" : "month"}
                          </span>
                        </div>

                        {isYearly && (
                          <div className="plan-upgrade-savings">
                            <motion.div
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.5 }}
                              className="notification is-success is-light">
                              Save {currencySymbol}
                              {(
                                parseFloat(plan.price) * 12 * yearlyDiscount
                              ).toFixed(0)}{" "}
                              with annual billing!
                            </motion.div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Custom text for Enterprise plans instead of price */}
                    {isEnterprise && (
                      <div className="plan-upgrade-price plan-upgrade-price-enterprise">
                        <span className="has-text-weight-semibold has-text-grey is-size-5">
                          Custom Pricing
                        </span>
                      </div>
                    )}

                    {/* Features List */}
                    {plan.features && plan.features.length > 0 && (
                      <div className="plan-upgrade-features">
                        <h4 className="title is-6 mb-3">What's included:</h4>
                        <ul className="plan-upgrade-features-list">
                          {plan.features.slice(0, 5).map((feature: any, featureIndex: number) => (
                            <motion.li
                              key={featureIndex}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.4 + featureIndex * 0.1 }}
                              className="plan-upgrade-feature-item">
                              <Check className="plan-upgrade-check-icon" size={20} />
                              <span>
                                {feature.PlanFeature?.value !== "true" &&
                                feature.PlanFeature?.value !== "false"
                                  ? `${feature.PlanFeature?.value} `
                                  : ""}
                                {feature.description}
                              </span>
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Trial Info */}
                    {plan.trial_days && plan.trial_days > 0 && (
                      <div className="plan-upgrade-trial">
                        <Zap size={16} />
                        <span>{plan.trial_days} days free trial included</span>
                      </div>
                    )}

                    {/* Action Button - Upgrade or Enterprise */}
                    {isEnterprise ? (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => openExternalLink(InfoService.loginLink)}
                        className="button is-fullwidth plan-upgrade-btn plan-enterprise-btn">
                        <MessageCircle size={20} />
                        <span>Get Started</span>
                      </motion.button>
                    ) : (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() =>openExternalLink(InfoService.loginLink)}
                        disabled={isProcessing || processingPlanId !== null}
                        className={`button is-fullwidth plan-upgrade-btn ${
                          isProcessing ? "is-loading" : ""
                        }`}>
                        {!isProcessing && (
                          <>
                            <span>Get Started </span>
                            <ArrowRight size={20} />
                          </>
                        )}
                      </motion.button>
                    )}
                  </div>
                </motion.div>
                </div>
              );
            })}
          </div>

          {/* Footer Note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="plan-upgrade-footer">
            <p className="has-text-centered has-text-grey is-size-7">
              Secure payment powered by Razorpay. Cancel anytime.
            </p>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default PlanUpgrade;
